import React, { useState } from 'react';
import { shortWon, Vehicle } from '../types/vehicle';
import { mult } from '../lib/metrics';

/**
 * 펀드 라이프사이클 · 수익률 시뮬레이션 — factsheet 「펀드 관리」.
 *
 * 라이프사이클은 시작 · 투자기간 종료 · 만기로 그린다. 시작은 결성일, 없으면 등록일 · 운용 개시일(2026-11-02 —
 * 개인투자조합 1호처럼 결성일 확인 전이거나 규약에 투자기간이 없는 조합도 그린다). 만기가 없으면 「날짜 없음」.
 *
 * 시뮬레이션은 **가정**이다 — 남은 평가액을 몇 배에 판다면. 저장하지 않는다.
 */
const DAY = 86_400_000;

export const FundLifecycle: React.FC<{
  vehicle: Vehicle; invested: number; returned: number; residual: number;
}> = ({ vehicle, invested, returned, residual }) => {
  const m = vehicle;
  const vehicleId = vehicle.id;
  const [x, setX] = useState(1.5);

  const start = m.formedOn ?? m.registeredOn ?? m.startedOn ?? null;
  const startLabel = m.formedOn ? '결성' : m.registeredOn ? '등록' : '운용 개시';
  const ready = !!(start && m.maturityOn);
  const hasInvest = ready && !!m.investEndOn;
  const t0 = ready ? new Date(start!).getTime() : 0;
  const t1 = hasInvest ? new Date(m.investEndOn!).getTime() : t0;
  const t2 = ready ? new Date(m!.maturityOn!).getTime() : 1;
  const now = Date.now();
  const at = (t: number) => `${Math.min(100, Math.max(0, ((t - t0) / (t2 - t0)) * 100))}%`;
  const left = ready ? Math.max(0, Math.ceil((t2 - now) / DAY)) : 0;
  const investLeft = ready ? Math.max(0, Math.ceil((t1 - now) / DAY)) : 0;

  const projected = returned + residual * x;
  const tvpi = invested > 0 ? projected / invested : null;

  return (
    <div data-tour="lifecycle" className="grid gap-4 lg:grid-cols-2 mb-5">
      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900">라이프사이클</h2>
          {m?.vicsCode && <span className="text-xs text-slate-500">VICS <b className="font-mono font-medium text-slate-700">{m.vicsCode}</b></span>}
        </div>
        {!ready ? (
          <p className="py-6 text-center text-sm text-slate-400">날짜 없음 · 자금원 수정에서 규약 값을 넣으세요</p>
        ) : (
          <>
            <div className="relative h-3 rounded-full overflow-hidden flex">
              {hasInvest && <div className="bg-emerald-400" style={{ width: at(t1) }} />}
              <div className="bg-indigo-400 flex-1" />
              <div className="absolute top-0 bottom-0 w-0.5 bg-slate-900" style={{ left: at(now) }} aria-hidden="true" />
            </div>
            <div className="flex justify-between mt-1.5 text-xs text-slate-500 tabular-nums">
              <span>{start!.replace(/-/g, '.')}<br />{startLabel}</span>
              {hasInvest && <span className="text-center">{m.investEndOn!.replace(/-/g, '.')}<br />투자기간 종료</span>}
              <span className="text-right">{m!.maturityOn!.replace(/-/g, '.')}<br />만기</span>
            </div>
            <div className="flex gap-4 mt-3 text-xs">
              {hasInvest && <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" />투자기간 {investLeft > 0 ? `D-${investLeft}` : '끝남'}</span>}
              <span><span className="inline-block w-2 h-2 rounded-full bg-indigo-400 mr-1" />만기 D-{left}</span>
            </div>
          </>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">시뮬레이션</h2>
        <label htmlFor={`sim-${vehicleId}`} className="flex items-center justify-between text-xs text-slate-500">
          <span>남은 평가액 {shortWon(residual)}을</span>
          <b className="text-sm text-slate-900 tabular-nums">{x.toFixed(1)}배에 판다면</b>
        </label>
        <input id={`sim-${vehicleId}`} type="range" min={0} max={4} step={0.1} value={x}
          onChange={(e) => setX(Number(e.target.value))} className="w-full mt-2 accent-indigo-600" disabled={residual === 0} />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div><div className="text-xs text-slate-400">예상 총회수</div><div className="text-base font-bold tabular-nums">{shortWon(projected)}</div></div>
          <div><div className="text-xs text-slate-400">예상 TVPI</div><div className="text-base font-bold tabular-nums text-indigo-600">{mult(tvpi)}</div></div>
        </div>
      </section>
    </div>
  );
};
